import {
  CredentialsFormState,
  GoogleIdentity,
  StandDetailsFormState,
  ValidateExhibitorResponse,
} from "view/SignUp/SignUpToken/context/types";

/**
 * What survives the trip to Google and back.
 *
 * Signing up with Google is a full-page redirect — the same mechanism the
 * login and invite pages use — so the form is unmounted and remounted. The
 * design puts the button above the fields, which makes pressing it first the
 * natural order and this restore a rare path; it exists because the person can
 * also press it after typing, and cancelling at Google must not cost them
 * their answers.
 *
 * `sessionStorage`, not `localStorage`: this belongs to one tab and one
 * attempt, and it is dropped as soon as the sign-up completes.
 */
export type PersistedSignUpForm = {
  token: string;
  showId: string | null;
  credentials: CredentialsFormState;
  validatedCredentials: CredentialsFormState;
  validationResult: ValidateExhibitorResponse | null;
  googleIdentity: GoogleIdentity | null;
  standDetails: StandDetailsFormState;
  activeStep: number;
};

const STORAGE_KEY = "ffair_embedded_sign_up_form";

export const persistSignUpForm = (form: PersistedSignUpForm): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(form));
  } catch {
    // Losing the draft only costs re-typing; never block the redirect for it.
  }
};

/**
 * Reads without removing. The caller clears it once the draft is safely back in
 * component state — a read that also removed could not be repeated, and
 * `React.StrictMode` invokes render twice in development.
 */
export const readSignUpForm = (
  token: string,
  showId: string | null
): PersistedSignUpForm | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) return null;

    const form = JSON.parse(raw) as PersistedSignUpForm;

    return form.token === token && form.showId === showId ? form : null;
  } catch {
    return null;
  }
};

export const clearSignUpForm = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the key expires with the tab anyway.
  }
};
